'use client';

import { useMemo } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { CheckmarkCircle01Icon, Clock01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { ReadOnlyCodeEditor } from '../../ReadOnlyCodeEditor';

type CoderRendererProps = {
  data: unknown;
  maxHeight?: string;
};
type ParsedCoderData = {
  script?: string;
  error?: string;
  output?: {
    success?: boolean;
    result?: string;
    codeRuntime?: number;
    stdout?: string[];
    stderr?: string[];
  };
};

function parseCoderData(data: unknown): ParsedCoderData {
  let obj: Record<string, unknown>;

  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else if (typeof data === 'object' && data !== null) {
    obj = data as Record<string, unknown>;
  } else {
    return {};
  }

  const parsed: ParsedCoderData = {};

  if (typeof obj.script === 'string') {
    parsed.script = obj.script;
  }

  if (typeof obj.error === 'string') {
    parsed.error = obj.error;
  }

  if (typeof obj.output === 'string') {
    try {
      const outputObj = JSON.parse(obj.output);

      parsed.output = {
        success: outputObj.success,
        result: outputObj.result,
        codeRuntime: outputObj.codeRuntime,
        stdout: outputObj.stdout,
        stderr: outputObj.stderr,
      };
    } catch {
      parsed.output = {
        result: obj.output,
      };
    }
  } else if (typeof obj.output === 'object' && obj.output !== null) {
    const outputObj = obj.output as Record<string, unknown>;

    parsed.output = {
      success:
        typeof outputObj.success === 'boolean' ? outputObj.success : undefined,
      result:
        typeof outputObj.result === 'string' ? outputObj.result : undefined,
      codeRuntime:
        typeof outputObj.codeRuntime === 'number'
          ? outputObj.codeRuntime
          : undefined,
      stdout: Array.isArray(outputObj.stdout) ? outputObj.stdout : undefined,
      stderr: Array.isArray(outputObj.stderr) ? outputObj.stderr : undefined,
    };
  }

  if (!parsed.output && typeof obj.result === 'string') {
    parsed.output = {
      result: obj.result,
      codeRuntime:
        typeof obj.timing === 'object' && obj.timing !== null
          ? ((obj.timing as Record<string, unknown>).durationMs as
              | number
              | undefined)
          : undefined,
    };
  }

  return parsed;
}

function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) {
    return `${milliseconds}ms`;
  }

  return `${(milliseconds / 1000).toFixed(2)}s`;
}

const SECTION_MAX_HEIGHT = '200px';

export function CoderRenderer({
  data,
  maxHeight = '450px',
}: CoderRendererProps) {
  const t = useTranslations('designSystem.chat.coder');
  const parsedData = useMemo(() => parseCoderData(data), [data]);
  const { script, output, error: topLevelError } = parsedData;

  if (!script && !output && !topLevelError) {
    return null;
  }

  let outputContent = '';

  if (output?.stdout && output.stdout.length > 0) {
    outputContent = output.stdout.join('\n');
  } else if (output?.result) {
    outputContent = output.result;
  }

  const stderrContent =
    output?.stderr && output.stderr.length > 0 ? output.stderr.join('\n') : '';
  const hasSuccessfulOutput = outputContent && output?.success !== false;
  const hasError = topLevelError || stderrContent || output?.success === false;
  const isErrorOnly = hasError && !outputContent;

  return (
    <VStack width="100%" maxHeight={maxHeight} gap={2} alignItems="stretch">
      {/* Script Section */}
      {script && (
        <Box width="100%" flexShrink={0}>
          <Box
            px={2}
            py={1.5}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.default"
            borderTopRadius="md"
          >
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('script')}
            </Text>
          </Box>
          <Box
            bg="bg.subtle"
            borderBottomRadius="md"
            overflow="hidden"
            maxHeight={SECTION_MAX_HEIGHT}
            overflowY="auto"
          >
            <ReadOnlyCodeEditor
              value={script}
              maxHeight={SECTION_MAX_HEIGHT}
              language="python"
            />
          </Box>
        </Box>
      )}

      {/* Success Output Section */}
      {hasSuccessfulOutput && (
        <Box width="100%" flexShrink={0}>
          <Box
            px={2}
            py={1.5}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.default"
            borderTopRadius="md"
          >
            <HStack justify="space-between" alignItems="center">
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                {t('output')}
              </Text>
              <HStack gap={1.5} alignItems="center">
                {output?.success === true && (
                  <Badge
                    size="sm"
                    variant="emphasis"
                    icon={<CheckmarkCircle01Icon size={10} />}
                  >
                    {t('success')}
                  </Badge>
                )}
                {output?.codeRuntime !== undefined && (
                  <Badge
                    size="sm"
                    variant="outline"
                    icon={<Clock01Icon size={10} />}
                    fontWeight="medium"
                  >
                    {formatDuration(output.codeRuntime)}
                  </Badge>
                )}
              </HStack>
            </HStack>
          </Box>
          <Box
            bg="bg.subtle"
            borderBottomRadius="md"
            overflow="hidden"
            maxHeight={SECTION_MAX_HEIGHT}
            overflowY="auto"
          >
            <ReadOnlyCodeEditor
              value={outputContent}
              maxHeight={SECTION_MAX_HEIGHT}
              language="text"
            />
          </Box>
        </Box>
      )}

      {/* Error Section - shown when there's an error (stderr or top-level error) */}
      {hasError && (
        <Box width="100%" flexShrink={0}>
          <Box
            px={2}
            py={1.5}
            bg={isErrorOnly ? 'bg.muted' : 'bg.canvas'}
            borderBottomWidth="1px"
            borderColor="border.default"
            borderTopRadius="md"
          >
            <HStack justify="space-between" alignItems="center">
              <Text
                fontSize="xs"
                fontWeight="medium"
                color={isErrorOnly ? 'fg.error' : 'fg.muted'}
              >
                {isErrorOnly ? t('executionFailed') : t('errors')}
              </Text>
              {output?.codeRuntime !== undefined && isErrorOnly && (
                <Badge
                  size="sm"
                  variant="outline"
                  icon={<Clock01Icon size={10} />}
                  fontWeight="medium"
                >
                  {formatDuration(output.codeRuntime)}
                </Badge>
              )}
            </HStack>
          </Box>
          <Box
            bg="bg.subtle"
            borderBottomRadius="md"
            overflow="hidden"
            maxHeight={SECTION_MAX_HEIGHT}
            overflowY="auto"
            p={2}
          >
            {stderrContent ? (
              <ReadOnlyCodeEditor
                value={stderrContent}
                maxHeight={SECTION_MAX_HEIGHT}
                language="text"
              />
            ) : topLevelError ? (
              <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                {topLevelError}
              </Text>
            ) : null}
          </Box>
        </Box>
      )}
    </VStack>
  );
}
